import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getOrder, approveArtwork } from "../lib/api";
import TurfCanvas from "../components/TurfCanvas";

const money = (v, sym = "£") => sym + Number(v || 0).toFixed(2);
const PIPELINE = [
  ["paid", "Paid"], ["artwork_review", "Artwork Review"], ["awaiting_approval", "Proof Sent / Awaiting Approval"],
  ["artwork_approved", "Artwork Approved"], ["in_production", "In Production"], ["quality_check", "Quality Check"],
  ["dispatched", "Dispatched"], ["completed", "Completed"],
];

export default function OrderStatus() {
  const { orderNumber } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => getOrder(orderNumber, token).then(setOrder).catch(() => setErr(true));
  useEffect(() => { if (token) load(); else setErr(true); /* eslint-disable-next-line */ }, [orderNumber, token]);

  if (err) return <div className="min-h-screen bg-pgt-bg"><div className="max-w-xl mx-auto px-6 py-24 text-center text-white/60">Order not found or link is invalid.</div></div>;
  if (!order) return <div className="min-h-screen bg-pgt-bg" />;

  const currentIdx = PIPELINE.findIndex(([s]) => s === order.status);
  const approve = async () => { await approveArtwork(orderNumber, token); toast.success("Artwork approved — production can begin."); load(); };

  return (
    <div className="min-h-screen bg-pgt-bg">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
        <div className="pgt-eyebrow mb-2">Your Order</div>
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <h1 className="pgt-heading text-white text-4xl sm:text-5xl">{order.order_number}</h1>
          <span className="bg-pgt-green/15 text-pgt-green font-display font-bold uppercase tracking-wide text-sm px-3 py-1 rounded-sm" data-testid="order-status">{(PIPELINE.find(([s]) => s === order.status) || [null, order.status])[1]}</span>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="space-y-6">
            <div className="border border-white/10 bg-[#111] rounded-sm h-[340px]"><TurfCanvas design={order.design} readOnly showSafe={false} /></div>

            <div className="border border-white/10 bg-pgt-surface rounded-sm p-5">
              <h3 className="font-display font-bold uppercase tracking-wider text-white mb-4">Progress</h3>
              <div className="space-y-3">
                {PIPELINE.map(([s, label], i) => (
                  <div key={s} className="flex items-center gap-3" data-testid={`pipeline-${s}`}>
                    <span className={`w-3 h-3 rounded-full ${i <= currentIdx ? "bg-pgt-green" : "bg-white/15"}`} />
                    <span className={`text-sm ${i <= currentIdx ? "text-white" : "text-white/40"}`}>{label}</span>
                  </div>
                ))}
              </div>
              {order.status === "awaiting_approval" && !order.artwork_approved && (
                <button onClick={approve} data-testid="approve-artwork-btn" className="mt-5 bg-pgt-green text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-pgt-greenHover">Approve Artwork</button>
              )}
              {order.tracking && <p className="mt-4 text-sm text-white/70">Tracking: <span className="text-white">{order.tracking}</span></p>}
            </div>
          </div>

          <div className="border border-white/10 bg-pgt-surface rounded-sm p-5 h-fit">
            <div className="pgt-eyebrow mb-3">Specification</div>
            <div className="space-y-1.5 text-sm">
              <Row l="Dimensions" v={`${order.design.dimensions.length}m × ${order.design.dimensions.width}m`} />
              <Row l="Area" v={`${(order.design.dimensions.length * order.design.dimensions.width).toFixed(2)} m²`} />
              <Row l="Turf colour" v={order.design.turf_colour_name} />
              <Row l="Production" v={order.production_label} />
              <Row l="Total paid" v={money(order.price.total, order.price.currency_symbol)} />
            </div>
            <p className="mt-4 text-xs text-white/40">Production begins once your artwork is approved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between gap-3"><span className="text-white/55">{l}</span><span className="text-white text-right">{v}</span></div>;
